import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  ShoppingCart as OrderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  Receipt as InvoiceIcon,
  Note as NoteIcon,
  Phone as PhoneIcon,
  Inventory as InventoryIcon,
  SwapHoriz as UpdateIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import OrderActivity from '../../models/orderActivity';

const OrderActivityHistory = ({ orderId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    activity_type: 'NOTE',
    title: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const activityTypeIcons = {
    ORDER_CREATED: <OrderIcon color="success" />,
    ORDER_UPDATED: <EditIcon color="primary" />,
    LINE_ITEM_ADDED: <InventoryIcon color="success" />,
    LINE_ITEM_UPDATED: <UpdateIcon color="primary" />,
    LINE_ITEM_REMOVED: <DeleteIcon color="error" />,
    STATUS_CHANGED: <UpdateIcon color="info" />,
    PAYMENT_RECEIVED: <PaymentIcon color="success" />,
    INVOICE_SENT: <InvoiceIcon color="primary" />,
    ORDER_CANCELLED: <CancelIcon color="error" />,
    NOTE: <NoteIcon color="secondary" />,
    CUSTOMER_CONTACT: <PhoneIcon color="primary" />
  };

  const activityTypeLabels = {
    ORDER_CREATED: 'Tạo đơn hàng',
    ORDER_UPDATED: 'Cập nhật đơn hàng',
    LINE_ITEM_ADDED: 'Thêm sản phẩm',
    LINE_ITEM_UPDATED: 'Cập nhật sản phẩm',
    LINE_ITEM_REMOVED: 'Xóa sản phẩm',
    STATUS_CHANGED: 'Thay đổi trạng thái',
    PAYMENT_RECEIVED: 'Nhận thanh toán',
    INVOICE_SENT: 'Gửi hóa đơn',
    ORDER_CANCELLED: 'Hủy đơn hàng',
    NOTE: 'Ghi chú',
    CUSTOMER_CONTACT: 'Liên hệ khách hàng'
  };

  const activityTypeColors = {
    ORDER_CREATED: 'success',
    ORDER_UPDATED: 'primary',
    LINE_ITEM_ADDED: 'success',
    LINE_ITEM_UPDATED: 'primary',
    LINE_ITEM_REMOVED: 'error',
    STATUS_CHANGED: 'info',
    PAYMENT_RECEIVED: 'success',
    INVOICE_SENT: 'primary',
    ORDER_CANCELLED: 'error',
    NOTE: 'secondary',
    CUSTOMER_CONTACT: 'primary'
  };

  useEffect(() => {
    if (orderId) {
      fetchActivities();
    }
  }, [orderId]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const result = await OrderActivity.getByOrderId(orderId);
      setActivities(result.data || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
      toast.error('Khong the tai lich su hoat dong');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.activity_type) errors.activity_type = 'Vui long chon loai hoat dong';
    if (!formData.title) errors.title = 'Vui long nhap tieu de';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddEntry = () => {
    setFormData({
      activity_type: 'NOTE',
      title: '',
      description: ''
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleSaveEntry = async () => {
    if (!validateForm()) return;

    try {
      const activity = new OrderActivity({
        order_id: orderId,
        activity_type: formData.activity_type,
        title: formData.title,
        description: formData.description
      });
      const result = await activity.save();

      if (result.success) {
        toast.success(result.message, {
          position: "top-center",
          autoClose: 3000
        });
        setDialogOpen(false);
        fetchActivities();
      } else {
        toast.error(result.message, {
          position: "top-center",
          autoClose: 3000
        });
      }
    } catch (error) {
      console.error('Error saving activity:', error);
      toast.error('Da xay ra loi khi luu', {
        position: "top-center",
        autoClose: 3000
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // API returns GMT+7 datetime with timezone info, parse and format directly
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography>Dang tai lich su hoat dong...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" color="primary">
              Lich su hoat dong don hang
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddEntry}
              size="small"
            >
              Them ghi chu
            </Button>
          </Box>

          {activities.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5' }}>
              <Typography variant="body1" color="text.secondary">
                Chua co lich su hoat dong nao
              </Typography>
            </Paper>
          ) : (
            <List sx={{ width: '100%' }}>
              {activities.map((activity, index) => (
                <React.Fragment key={activity.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      '&:hover': { bgcolor: '#f5f5f5' },
                      py: 2
                    }}
                  >
                    <ListItemIcon sx={{ mt: 1 }}>
                      {activityTypeIcons[activity.activity_type] || <NoteIcon />}
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box flex={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {activity.title}
                            </Typography>
                            <Box display="flex" gap={1} alignItems="center" mt={0.5}>
                              <Chip
                                size="small"
                                label={activityTypeLabels[activity.activity_type] || activity.activity_type}
                                color={activityTypeColors[activity.activity_type] || 'default'}
                              />
                            </Box>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box mt={1}>
                          {activity.description && (
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {activity.description}
                            </Typography>
                          )}
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <TimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(activity.created_at)}
                              {activity.performer && ` - ${activity.performer.name}`}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Them ghi chu / lien he
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth error={!!formErrors.activity_type}>
              <InputLabel>Loai hoat dong *</InputLabel>
              <Select
                value={formData.activity_type}
                onChange={(e) => setFormData({ ...formData, activity_type: e.target.value })}
                label="Loai hoat dong *"
              >
                <MenuItem value="NOTE">Ghi chu</MenuItem>
                <MenuItem value="CUSTOMER_CONTACT">Lien he khach hang</MenuItem>
              </Select>
              {formErrors.activity_type && (
                <Typography variant="caption" color="error">{formErrors.activity_type}</Typography>
              )}
            </FormControl>

            <TextField
              fullWidth
              label="Tieu de *"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              error={!!formErrors.title}
              helperText={formErrors.title}
            />

            <TextField
              fullWidth
              label="Mo ta chi tiet"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Huy</Button>
          <Button onClick={handleSaveEntry} variant="contained">
            Them moi
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderActivityHistory;
